import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Call,
  Conversation,
  Group,
  User,
  CometChat,
} from "@cometchat/chat-sdk-javascript";
import {
  CometChatButton,
  CometChatCallLogs,
  CometChatConversations,
  CometChatGroups,
  CometChatOption,
  CometChatUIKit,
  CometChatUIKitLoginListener,
  CometChatUsers,
  getLocalizedString,
} from "@cometchat/chat-uikit-react";
import { CometChatContextMenu, Placement } from "@cometchat/chat-uikit-react";

import "../../styles/CometChatSelector/CometChatSelector.css";
import chatIcon from "../../assets/start_chat.svg";
import createGroupIcon from "../../assets/create-group.svg";
import logoutIcon from "../../assets/logout.svg";
import userIcon from "../../assets/user.svg";

import { AppContext } from "../../context/AppContext";
import { CometChatJoinGroup } from "../CometChatJoinGroup/CometChatJoinGroup";
import CometChatCreateGroup from "../CometChatCreateGroup/CometChatCreateGroup";


interface SelectorProps {
  group?: Group;
  showJoinGroup?: boolean;
  activeTab?: string;
  activeItem?: User | Group | Conversation | Call;
  onSelectorItemClicked?: (input: User | Group | Conversation | Call, type: string) => void;
  onProtectedGroupJoin?: (group: Group) => void;
  showCreateGroup?: boolean;
  setShowCreateGroup?: Dispatch<SetStateAction<boolean>>;
  onHide?: () => void;
  onNewChatClicked?: () => void;
  onGroupCreated?: (group: Group) => void;
}

export const CometChatSelector = (props: SelectorProps) => {
  const {
    group,
    showJoinGroup,
    activeItem,
    activeTab,
    onSelectorItemClicked = () => { },
    onProtectedGroupJoin = () => { },
    showCreateGroup,
    setShowCreateGroup = () => { },
    onHide = () => { },
    onNewChatClicked = () => { },
    onGroupCreated = () => { },
  } = props;

  const [loggedInUser, setLoggedInUser] = useState<CometChat.User | null>(null);
  const [friendsList, setFriendsList] = useState<User[]>([]);
  const navigate = useNavigate();
  const { setAppState } = useContext(AppContext);

  // Fetch logged in user
  useEffect(() => {
    const user = CometChatUIKitLoginListener.getLoggedInUser();
    setLoggedInUser(user);
  }, []);

  // Fetch friends list when activeTab is 'users'
  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const friendsRequest = new CometChat.UsersRequestBuilder()
          .friendsOnly(true)
          .setLimit(50)
          .build();
        const users = await friendsRequest.fetchNext();
        setFriendsList(users);
      } catch (err) {
        console.error("Error fetching friends:", err);
      }
    };

    if (activeTab === "users") {
      fetchFriends();
    }
  }, [activeTab]);

  const logOut = () => {
    CometChatUIKit.logout()
      .then(() => {
        setLoggedInUser(null);
        setAppState({ type: "resetAppState" });
        navigate("/login", { replace: true });
      })
      .catch((error) => {
        console.error("Logout error", error);
      });
  };

  const getOptions = (): CometChatOption[] => [
    new CometChatOption({
      id: "logged-in-user",
      title: loggedInUser?.getName() || "",
      iconURL: userIcon,
    }),
    new CometChatOption({
      id: "create-conversation",
      title: getLocalizedString("create_conversation"),
      iconURL: chatIcon,
      onClick: onNewChatClicked,
    }),
    new CometChatOption({
      id: "log-out",
      title: getLocalizedString("log_out"),
      iconURL: logoutIcon,
      onClick: logOut,
    }),
  ];



  const groupsHeaderView = () => (
    <div className="cometchat-groups-header">
      <div className="cometchat-groups-header__title">
        {getLocalizedString("group_title")}
      </div>
      <CometChatButton
        iconURL={createGroupIcon}
        onClick={() => setShowCreateGroup(true)}
      />
    </div>
  );

  // Memoize the usersRequestBuilder so it is not recreated on every render
  const usersRequestBuilder = useMemo(() => {
    if (activeTab === "users") {
      return new CometChat.UsersRequestBuilder()
        .friendsOnly(true)
        .setLimit(50);
    }
    return null;
  }, [activeTab]);
  
  return (
    <>
      {loggedInUser && (
        <>
          {showJoinGroup && group && (
            <CometChatJoinGroup
              group={group}
              onHide={onHide}
              onProtectedGroupJoin={onProtectedGroupJoin}
            />
          )}

          {activeTab === "chats" && (
            <CometChatConversations
              activeConversation={activeItem as Conversation}
              // headerView={conversationsHeaderView(activeTab)} 
              headerView={<></>}
              // showSearchBar={true}
              onItemClick={(e) => onSelectorItemClicked(e, "updateSelectedItem")}
              loadingView={<></>}
            />
          )}

          {activeTab === "calls" && (
            <CometChatCallLogs
              activeCall={activeItem as Call}
              onItemClick={(e) => onSelectorItemClicked(e, "updateSelectedItemCall")}
            />
          )}

          {activeTab === "users" && (
            <CometChatUsers
              usersRequestBuilder={usersRequestBuilder}
              activeUser={activeItem as User}
              onItemClick={(e) => onSelectorItemClicked(e, "updateSelectedItemUser")}
              headerView={<></>}
              // headerView={conversationsHeaderView(activeTab)}
              showSectionHeader={false}
              loadingView={<></>}
            />
          )}

          {activeTab === "groups" && (
            <CometChatGroups
              activeGroup={activeItem as Group}
              headerView={groupsHeaderView()}
              onItemClick={(e) => onSelectorItemClicked(e, "updateSelectedItemGroup")}
            />
          )}

          {showCreateGroup && (
            <CometChatCreateGroup
              setShowCreateGroup={setShowCreateGroup}
              onGroupCreated={onGroupCreated}
            />
          )}
        </>
      )}
    </>
  );
};